import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, TrendingUp, Users, CreditCard, Banknote, RefreshCw, Printer, ShoppingBag, Bike, QrCode, Building2, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const METHOD_LABELS = {
  cash: { label: 'Especes', icon: Banknote, color: '#2D6A4F' },
  card_terminal: { label: 'Carte bancaire', icon: CreditCard, color: '#6366F1' },
  promptpay: { label: 'PromptPay', icon: QrCode, color: '#1B3A4B' },
  counter: { label: 'Comptoir', icon: Building2, color: '#0A422D' },
  unknown: { label: 'Autre', icon: CreditCard, color: '#888' },
};

const SOURCE_LABELS = {
  dine_in: 'Sur place', takeaway: 'Emporter', click_collect: 'Click & Collect',
  grab: 'Grab', foodpanda: 'Foodpanda', uber_eats: 'Uber Eats', deliveroo: 'Deliveroo',
};

export const ZReportTab = ({ formatPrice }) => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const fp = formatPrice || ((p) => `฿${(p || 0).toLocaleString()}`);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/z-report?date=${date}`);
      setReport(res.data);
    } catch {} finally { setLoading(false); }
  }, [date]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/caisse/history?limit=60`);
      setHistory(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchReport(); }, [fetchReport]);
  useEffect(() => { if (showHistory) fetchHistory(); }, [showHistory, fetchHistory]);

  const printSessionTicket = (session) => {
    const cd = session.closing_data;
    if (!cd) return;
    const w = window.open('', '_blank', 'width=400,height=600');
    if (!w) return;

    const dateStr = new Date(session.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const openTime = session.created_at ? new Date(session.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-';
    const closeTime = session.closed_at ? new Date(session.closed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-';

    const methodRows = Object.entries(cd.by_payment_method || {}).map(([k, v]) => {
      const lbl = METHOD_LABELS[k]?.label || k;
      return `<tr><td>${lbl}</td><td style="text-align:center">${v.count}</td><td style="text-align:right">${fp(v.total)}</td></tr>`;
    }).join('');

    const sourceRows = Object.entries(cd.by_source || {}).map(([k, v]) => {
      const lbl = SOURCE_LABELS[k] || k;
      return `<tr><td>${lbl}</td><td style="text-align:center">${v.orders}</td><td style="text-align:right">${fp(v.total)}</td></tr>`;
    }).join('');

    w.document.write(`<!DOCTYPE html><html><head><style>
      @page{size:80mm auto;margin:3mm}
      body{font-family:'Courier New',monospace;font-size:11px;width:76mm;margin:0 auto;padding:4mm 0}
      .center{text-align:center}
      .bold{font-weight:bold}
      .big{font-size:16px;font-weight:bold}
      .line{border-top:1px dashed #000;margin:6px 0}
      .dline{border-top:2px double #000;margin:6px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:1px 0;vertical-align:top;font-size:11px}
    </style></head><body>
      <div class="center big">CLOTURE DE CAISSE</div>
      <div class="center">${dateStr}</div>
      <div class="center" style="font-size:10px">Ouverture: ${openTime} | Fermeture: ${closeTime}</div>
      <div class="center" style="font-size:9px">Ouvert par: ${session.opened_by || '-'} | Ferme par: ${session.closed_by || '-'}</div>
      <div class="center" style="font-size:9px">Reimpression le ${new Date().toLocaleString('fr-FR')}</div>
      <div class="dline"></div>

      <table>
        <tr><td class="bold">TOTAL COMMANDES</td><td style="text-align:right" class="bold">${cd.total_orders}</td></tr>
        <tr><td>Sur place</td><td style="text-align:right">${cd.dine_in_count || 0}</td></tr>
        <tr><td>Emporter</td><td style="text-align:right">${cd.takeaway_count || 0}</td></tr>
      </table>
      <div class="line"></div>

      <table>
        <tr><td class="bold big">CHIFFRE D'AFFAIRES</td><td style="text-align:right" class="bold big">${fp(cd.total_revenue)}</td></tr>
        ${cd.total_discounts > 0 ? `<tr><td>Remises</td><td style="text-align:right">-${fp(cd.total_discounts)}</td></tr>` : ''}
      </table>
      <div class="line"></div>

      ${methodRows ? `<div class="bold" style="font-size:12px">PAR MODE DE PAIEMENT</div>
      <table><tr><td class="bold">Mode</td><td style="text-align:center" class="bold">Nb</td><td style="text-align:right" class="bold">Total</td></tr>${methodRows}</table>
      <div class="line"></div>` : ''}

      ${sourceRows ? `<div class="bold" style="font-size:12px">PAR SOURCE</div>
      <table><tr><td class="bold">Source</td><td style="text-align:center" class="bold">Nb</td><td style="text-align:right" class="bold">Total</td></tr>${sourceRows}</table>
      <div class="line"></div>` : ''}

      <div class="bold" style="font-size:12px">CONTROLE CAISSE</div>
      <table>
        <tr><td>Fond de caisse</td><td style="text-align:right">${fp(session.opening_cash || 0)}</td></tr>
        <tr><td>Encaissements cash</td><td style="text-align:right">${fp(cd.cash_revenue || 0)}</td></tr>
        <tr><td class="bold">Cash attendu</td><td style="text-align:right" class="bold">${fp(cd.expected_cash || 0)}</td></tr>
        <tr><td>Cash compte</td><td style="text-align:right">${fp(cd.closing_cash_counted || 0)}</td></tr>
        <tr><td class="bold">ECART</td><td style="text-align:right" class="bold">${fp(cd.cash_difference || 0)}</td></tr>
      </table>
      <div class="dline"></div>
      <div class="center" style="font-size:9px">--- Fin du ticket ---</div>

      <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div>
      <script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
    </body></html>`);
    w.document.close();
  };

  const printZReport = () => {
    if (!report) return;
    const w = window.open('', '_blank', 'width=400,height=700');
    if (!w) return;

    const methodRows = Object.entries(report.by_payment_method || {}).map(([k, v]) => {
      const lbl = METHOD_LABELS[k]?.label || k;
      return `<tr><td>${lbl}</td><td style="text-align:center">${v.count}</td><td style="text-align:right">${fp(v.total)}</td></tr>`;
    }).join('');

    const serverRows = Object.entries(report.by_server || {}).map(([name, v]) =>
      `<tr><td>${name}</td><td style="text-align:center">${v.orders}</td><td style="text-align:right">${fp(v.total)}</td></tr>`
    ).join('');

    const sourceRows = Object.entries(report.by_source || {}).map(([k, v]) => {
      const lbl = SOURCE_LABELS[k] || k;
      return `<tr><td>${lbl}</td><td style="text-align:center">${v.orders}</td><td style="text-align:right">${fp(v.total)}</td></tr>`;
    }).join('');

    w.document.write(`<!DOCTYPE html><html><head><style>
      @page{size:80mm auto;margin:3mm}
      body{font-family:'Courier New',monospace;font-size:11px;width:76mm;margin:0 auto;padding:4mm 0}
      .center{text-align:center}
      .bold{font-weight:bold}
      .big{font-size:16px;font-weight:bold}
      .line{border-top:1px dashed #000;margin:6px 0}
      .dline{border-top:2px double #000;margin:6px 0}
      table{width:100%;border-collapse:collapse}
      td{padding:1px 0;vertical-align:top;font-size:11px}
      h3{font-size:12px;margin:4px 0 2px;text-transform:uppercase}
    </style></head><body>
      <div class="center big">CLOTURE DE CAISSE</div>
      <div class="center">Rapport Z</div>
      <div class="center" style="font-size:10px">${new Date(report.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
      <div class="center" style="font-size:9px">Imprime le ${new Date().toLocaleString('fr-FR')}</div>
      <div class="dline"></div>

      <table>
        <tr><td class="bold">TOTAL COMMANDES</td><td style="text-align:right" class="bold">${report.total_orders}</td></tr>
        <tr><td>Sur place</td><td style="text-align:right">${report.dine_in_count}</td></tr>
        <tr><td>Emporter / C&C</td><td style="text-align:right">${report.takeaway_count}</td></tr>
      </table>
      <div class="line"></div>

      <table>
        <tr><td class="bold big">CHIFFRE D'AFFAIRES</td><td style="text-align:right" class="bold big">${fp(report.total_revenue)}</td></tr>
        ${report.total_discounts > 0 ? `<tr><td>Remises</td><td style="text-align:right">-${fp(report.total_discounts)}</td></tr>` : ''}
        ${report.total_tips > 0 ? `<tr><td>Pourboires</td><td style="text-align:right">+${fp(report.total_tips)}</td></tr>` : ''}
        ${report.total_commissions > 0 ? `<tr><td>Commissions plat.</td><td style="text-align:right">${fp(report.total_commissions)}</td></tr>` : ''}
        <tr><td class="bold">Ticket moyen</td><td style="text-align:right" class="bold">${fp(report.avg_ticket)}</td></tr>
      </table>
      <div class="dline"></div>

      <h3>Par moyen de paiement</h3>
      <table><tr style="font-size:10px;border-bottom:1px solid #000"><td></td><td style="text-align:center">Nb</td><td style="text-align:right">Total</td></tr>${methodRows}</table>
      <div class="line"></div>

      <h3>Par serveur</h3>
      <table><tr style="font-size:10px;border-bottom:1px solid #000"><td></td><td style="text-align:center">Cmd</td><td style="text-align:right">Total</td></tr>${serverRows}</table>
      <div class="line"></div>

      <h3>Par source</h3>
      <table><tr style="font-size:10px;border-bottom:1px solid #000"><td></td><td style="text-align:center">Cmd</td><td style="text-align:right">Total</td></tr>${sourceRows}</table>
      <div class="dline"></div>

      <table>
        <tr><td>Commandes payees</td><td style="text-align:right">${report.paid_count}</td></tr>
        <tr><td>Non payees</td><td style="text-align:right">${report.unpaid_count}</td></tr>
      </table>
      <div class="line"></div>

      <h3>Controle Cash</h3>
      <table>
        <tr><td>Fond de caisse</td><td style="text-align:right">${fp(report.opening_cash || 0)}</td></tr>
        <tr><td>+ Cash encaisse</td><td style="text-align:right">${fp(report.cash_revenue || 0)}</td></tr>
        <tr><td class="bold">= Cash attendu</td><td style="text-align:right" class="bold">${fp(report.expected_cash || 0)}</td></tr>
      </table>
      <div class="line"></div>
      <div class="center" style="font-size:9px;margin-top:4px">--- FIN DU RAPPORT Z ---</div>
      <div id="pf" style="text-align:center;padding:10px;position:fixed;bottom:0;left:0;right:0;background:#fff;border-top:1px solid #ccc;z-index:999"><button onclick="window.print()" style="padding:12px 40px;font-size:16px;font-weight:bold;background:#0A422D;color:#fff;border:none;border-radius:8px;cursor:pointer">IMPRIMER</button></div>
      <script>setTimeout(function(){try{window.print()}catch(e){}},600)<\/script>
    </body></html>`);
    w.document.close();
  };

  if (loading || !report) {
    return <div className="flex items-center justify-center py-16"><RefreshCw className="w-6 h-6 animate-spin text-[#6E5E52]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="z-report-tab">
      {/* Header */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Calendar className="w-5 h-5 text-[#E16833]" />
          <h3 className="font-bold text-[#2D241D]">Cloture de caisse (Z)</h3>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="h-8 w-40 text-xs rounded-lg border-[#E5DCD3]" data-testid="z-report-date" />
        </div>
        <Button onClick={printZReport} className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl gap-2" data-testid="print-z-report">
          <Printer className="w-4 h-4" /> Imprimer Z
        </Button>
      </div>

      {/* Big stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-[#0A422D] rounded-2xl p-4 text-white">
          <p className="text-xs opacity-70 mb-1">Chiffre d'affaires</p>
          <p className="text-2xl font-black" data-testid="z-revenue">{fp(report.total_revenue)}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Commandes</p>
          <p className="text-2xl font-black text-[#2D241D]" data-testid="z-orders">{report.total_orders}</p>
          <p className="text-[10px] text-[#6E5E52]">{report.dine_in_count} sur place / {report.takeaway_count} emporter</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Ticket moyen</p>
          <p className="text-2xl font-black text-[#D4A853]" data-testid="z-avg-ticket">{fp(report.avg_ticket)}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Pourboires</p>
          <p className="text-2xl font-black text-[#2D6A4F]" data-testid="z-tips">{fp(report.total_tips)}</p>
          {report.total_discounts > 0 && <p className="text-[10px] text-red-500">Remises: -{fp(report.total_discounts)}</p>}
        </div>
        <div className="bg-[#D4A853]/10 rounded-2xl p-4 border border-[#D4A853]/30">
          <p className="text-xs text-[#6E5E52] mb-1">Fond de caisse</p>
          <p className="text-2xl font-black text-[#D4A853]" data-testid="z-opening-cash">{fp(report.opening_cash || 0)}</p>
          {report.expected_cash > 0 && <p className="text-[10px] text-[#6E5E52]">Cash attendu: {fp(report.expected_cash)}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* By payment method */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#6366F1]" /> Par moyen de paiement
            </h4>
          </div>
          <div className="divide-y divide-[#F3EDE5]">
            {Object.entries(report.by_payment_method || {}).map(([method, data]) => {
              const meta = METHOD_LABELS[method] || METHOD_LABELS.unknown;
              const Icon = meta.icon;
              return (
                <div key={method} className="px-4 py-2.5 flex items-center gap-3" data-testid={`z-method-${method}`}>
                  <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  <span className="text-xs font-medium text-[#2D241D] flex-1">{meta.label}</span>
                  <span className="text-xs text-[#6E5E52]">{data.count} paiements</span>
                  <span className="text-sm font-bold text-[#0A422D]">{fp(data.total)}</span>
                </div>
              );
            })}
            {Object.keys(report.by_payment_method || {}).length === 0 && (
              <p className="text-center text-xs text-[#6E5E52] py-4">Aucun paiement</p>
            )}
          </div>
        </div>

        {/* By server */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
              <Users className="w-4 h-4 text-[#D4A853]" /> Par serveur
            </h4>
          </div>
          <div className="divide-y divide-[#F3EDE5]">
            {Object.entries(report.by_server || {}).map(([name, data]) => (
              <div key={name} className="px-4 py-2.5 flex items-center gap-3" data-testid={`z-server-${name}`}>
                <span className="text-xs font-medium text-[#2D241D] flex-1">{name}</span>
                <span className="text-xs text-[#6E5E52]">{data.orders} cmd</span>
                <span className="text-sm font-bold text-[#0A422D]">{fp(data.total)}</span>
              </div>
            ))}
            {Object.keys(report.by_server || {}).length === 0 && (
              <p className="text-center text-xs text-[#6E5E52] py-4">Aucun serveur</p>
            )}
          </div>
        </div>

        {/* By source */}
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
              <Bike className="w-4 h-4 text-[#E16833]" /> Par source
            </h4>
          </div>
          <div className="divide-y divide-[#F3EDE5]">
            {Object.entries(report.by_source || {}).map(([src, data]) => (
              <div key={src} className="px-4 py-2.5 flex items-center gap-3" data-testid={`z-source-${src}`}>
                <span className="text-xs font-medium text-[#2D241D] flex-1">{SOURCE_LABELS[src] || src}</span>
                <span className="text-xs text-[#6E5E52]">{data.orders} cmd</span>
                <span className="text-sm font-bold text-[#0A422D]">{fp(data.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer stats */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-[#6E5E52]">
          <span className="text-green-600 font-bold">{report.paid_count} payees</span>
          {' / '}
          <span className="text-red-500 font-bold">{report.unpaid_count} non payees</span>
          {report.total_commissions > 0 && <span className="ml-3">Commissions plateformes: <strong className="text-[#E16833]">{fp(report.total_commissions)}</strong></span>}
        </div>
        {report.caisse_status && (
          <span className={`text-xs px-2 py-1 rounded-full font-bold ${report.caisse_status === 'closed' ? 'bg-red-100 text-red-600' : report.caisse_status === 'open' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
            Caisse: {report.caisse_status === 'closed' ? 'Cloturee' : report.caisse_status === 'open' ? 'Ouverte' : 'Non ouverte'}
          </span>
        )}
      </div>

      {/* Historique des sessions de caisse */}
      <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden" data-testid="caisse-history">
        <button onClick={() => setShowHistory(!showHistory)}
          className="w-full px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3] flex items-center justify-between hover:bg-[#F3EDE5] transition-colors">
          <h4 className="text-sm font-bold text-[#2D241D] flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#E16833]" /> Historique ouvertures / clotures
          </h4>
          {showHistory ? <ChevronUp className="w-4 h-4 text-[#6E5E52]" /> : <ChevronDown className="w-4 h-4 text-[#6E5E52]" />}
        </button>
        {showHistory && (
          <div className="divide-y divide-[#F3EDE5] max-h-[400px] overflow-y-auto">
            {history.length === 0 && <p className="text-center text-xs text-[#6E5E52] py-6">Aucune session enregistree</p>}
            {history.map((session, i) => {
              const cd = session.closing_data;
              const dateStr = new Date(session.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
              const openTime = session.created_at ? new Date(session.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-';
              const closeTime = session.closed_at ? new Date(session.closed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '-';
              return (
                <div key={session.id || i} className="px-4 py-3 hover:bg-[#FAF7F2] transition-colors" data-testid={`caisse-session-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#2D241D]">{dateStr}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${session.status === 'closed' ? 'bg-red-100 text-red-600' : session.status === 'open' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                        {session.status === 'closed' ? 'Cloturee' : session.status === 'open' ? 'Ouverte' : session.status}
                      </span>
                      <span className="text-[10px] text-[#6E5E52]">{openTime} → {closeTime}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-[#D4A853]">Fond: {fp(session.opening_cash || 0)}</span>
                      {session.closing_data && (
                        <button onClick={() => printSessionTicket(session)}
                          className="text-[10px] px-2 py-1 rounded-lg bg-[#0A422D] text-white font-bold flex items-center gap-1 hover:bg-[#083323] active:scale-95 transition-all"
                          data-testid={`print-session-${i}`}>
                          <Printer className="w-3 h-3" /> Reimprimer
                        </button>
                      )}
                    </div>
                  </div>
                  {cd && (
                    <div className="flex items-center gap-4 text-[10px] text-[#6E5E52]">
                      <span>CA: <strong className="text-[#0A422D]">{fp(cd.total_revenue)}</strong></span>
                      <span>Commandes: <strong>{cd.total_orders}</strong></span>
                      <span>Cash attendu: <strong>{fp(cd.expected_cash)}</strong></span>
                      <span>Compte: <strong>{fp(cd.closing_cash_counted)}</strong></span>
                      <span className={cd.cash_difference >= 0 ? 'text-green-600' : 'text-red-600'}>
                        Ecart: <strong>{cd.cash_difference >= 0 ? '+' : ''}{fp(cd.cash_difference)}</strong>
                      </span>
                      <span>Par: <strong>{session.opened_by || '-'}</strong> / <strong>{session.closed_by || '-'}</strong></span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
